import type {
  FieldInputType,
  FieldConfig,
  CustomField,
  FieldEntityTarget
} from '@gat-ship/types'
import type { ComputedRef, InjectionKey, Ref } from 'vue'

// ── Overrides returned by fieldProps() ──────────────────────────────

/** Props to spread onto a gat-form-field-based input to apply backend overrides */
export interface FieldConfigOverrides {
  required?: boolean | string
  description?: string
  readonly?: boolean
}

// ── System Field Registration ───────────────────────────────────────

/** Metadata registered by each CustomFieldSlot component on mount */
export interface SystemFieldRegistration {
  fieldName: string
  requiredByDeveloper?: boolean
}

// SystemFieldDefinition and ResolvedField are relocated to @gat-ship/types
// (so the runtime-free CustomFieldsAdminService contract in
// types/injection-keys can reference them without a types -> feature
// back-edge). Re-exported here verbatim to keep the package surface identical.
export type { SystemFieldDefinition, ResolvedField } from '@gat-ship/types'

// ── Resolved Types ──────────────────────────────────────────────────

/** A custom field resolved with its configuration, ready for rendering */
export interface ResolvedCustomField {
  fieldName: string
  label: string
  inputType: FieldInputType
  required: boolean
  disabled: boolean
  hint: string | null
  aiDescription: string | null
  numberOfDecimals: number | null
  dropdownOptions: string[] | null
}

// ── Context ─────────────────────────────────────────────────────────

/** The context provided by useCustomFields and injected by CustomFieldSlot / CustomFieldForm */
export interface CustomFieldsContext {
  target: Ref<FieldEntityTarget> | FieldEntityTarget
  loading: Ref<boolean>
  fieldConfigs: Ref<FieldConfig[]>
  customFieldDefinitions: Ref<CustomField[]>
  userRoleIds: ComputedRef<string[]>

  /** Get override props to spread onto a gat-form-field-based input */
  fieldProps: (fieldName: string) => FieldConfigOverrides

  /** Whether a named system field is visible after resolving config + roles */
  isSystemFieldVisible: (fieldName: string) => boolean

  /** All custom fields resolved for rendering (filtered for visibility + roles) */
  customFields: ComputedRef<ResolvedCustomField[]>

  /** Register a system field (called by CustomFieldSlot on mount) */
  registerSystemField: (registration: SystemFieldRegistration) => void

  /** Unregister a system field (called by CustomFieldSlot on unmount) */
  unregisterSystemField: (fieldName: string) => void
}

export const CUSTOM_FIELDS_INJECTION_KEY: InjectionKey<CustomFieldsContext> =
  Symbol('custom-fields-context')
